// THE LOBBY — the room you walk into before you sit down at a table.
//
// Silent by design. Nothing here starts the game's audio context, spawns a
// round, or mounts the wheel: Page.tsx is not rendered at all until a table has
// been chosen (see Entry.tsx). The owner asked for exactly that — "nothing
// shows up at first, no sound, no nothing" — and it is also what makes the
// first announcement land, because the first tap in here is the gesture that
// unlocks audio and speech for the session.
//
// ── WHY CARDS, AND WHY A BUTTON THAT SAYS WHAT IT DOES ───────────────────
// The first version was a list of rows. The owner's verdict: "it does not let a
// dumb user know that it is the place from which it has to select and can play
// the game... give some clear indication, PLAY NOW or something of that sort,
// and it should be a really big button with a proper playing icon."
//
// He is right, and the fault is a common one: the rows were TAPPABLE but
// nothing on them was a BUTTON. A row with information on it reads as
// information. Every card now ends in a gold PLAY NOW bar with a play triangle
// on it, which is the only element on the screen that looks pressable — so
// there is exactly one thing to do and it is impossible to miss.
//
// The card is still tappable end to end, so a thumb that lands anywhere still
// gets in. The button is the affordance; the card is the target.

import { useEffect, useState } from 'react';
import { press } from './press';
import {
  joinWaitMs, msToNextRound, recentResults, seatedAt, windowMsFor, type Table,
} from './tables';
import './lobby.css';

const colour = (n: number) =>
  n === 0 ? 'zero' : [1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36].includes(n) ? 'red' : 'black';

/** One shared 1 Hz tick for the whole screen — nine cards each running their
 *  own timer is nine timers doing the same job. */
function useSecond() {
  const [, bump] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => bump((v) => v + 1), 1000);
    return () => window.clearInterval(t);
  }, []);
  return Date.now();
}

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden><path d="M8 5.2v13.6L19 12z" /></svg>
);

/**
 * Live numbers for one table, when they are the SERVER's rather than derived (Phase E, §5.7).
 *
 * The prop is optional and the fallbacks below are the shipped local functions verbatim, so
 * with `roulette.shared_tables` off this component behaves exactly as it did — same countdown,
 * same seat count, same derived strip. With the flag on, `sharedLobby.ts` supplies real ones
 * and NOTHING here is derived: a real table's record must never be a fiction the player can
 * catch by comparing screens.
 */
export type LobbyLive = { left: number; hist: number[]; seated: number };

export function Lobby({ tables, onPick, onExit, live, note }: {
  tables: Table[];
  onPick: (t: Table) => void;
  /** leave roulette for the games list -- the lobby's only door out */
  onExit?: () => void;
  /** shared mode only: real countdown / real record / real+fake occupancy, keyed by table id */
  live?: Record<string, LobbyLive>;
  /** a line under the title (shared mode uses it while the table list is still loading) */
  note?: string | null;
}) {
  const now = useSecond();
  return (
    <div className="rlt-lobby">
      <div className="rlt-lobby-head">
        {onExit && (
          <button type="button" className="rlt-lobby-back" aria-label="Back to games"
                  {...press(onExit)}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M15.4 5.4 8.8 12l6.6 6.6-1.9 1.9L5 12l8.5-8.5z" />
            </svg>
          </button>
        )}
        <div className="rlt-lobby-title">Roulette</div>
        {/* Plain instruction, not a label. "Choose a table" describes the
            screen; this tells them what to do with it. */}
        <div className="rlt-lobby-sub">{note || 'Tap PLAY NOW on any table to join'}</div>
      </div>

      <div className="rlt-lobby-grid">
        {tables.map((t) => {
          const L = live?.[t.id];
          const left = L ? L.left : Math.max(0, Math.ceil(msToNextRound(t, now) / 1000));
          const hist = L ? L.hist.slice(0, 5) : recentResults(t, now, 5);
          const seated = L ? L.seated : seatedAt(t, now);
          return (
            <div key={t.id} className={`rlt-card ${t.fast ? 'rlt-card--quick' : ''}`}
                 {...press(() => onPick(t))} role="button" tabIndex={0}>
              <div className="rlt-card-top">
                <div className="rlt-card-name">{t.name}</div>
                {/* QUICK IS THE ROOM'S OWN PACE, chosen here and nowhere else.
                    It used to be a switch inside the game; the owner moved it
                    out — "it should not be able to switch off the quick session,
                    it should be based on the session that it has selected". */}
                <span className={`rlt-card-pace ${t.fast ? 'quick' : ''}`}>
                  {t.fast ? '⚡ QUICK' : `${windowMsFor(t) / 1000}s`}
                </span>
              </div>

              <div className="rlt-card-meta">
                <span className="rlt-card-id">#{t.id}</span>
                <span className="rlt-card-dot" />
                <span>{seated.toLocaleString('en-IN')} playing</span>
                <span className="rlt-card-dot" />
                <span>min ₹{t.minBet}</span>
              </div>

              {/* the table's last five, so it has visibly been running before
                  anyone touched it.
                  In shared mode these are the numbers the table REALLY produced, and a table
                  that has not settled a round yet shows nothing rather than a derived strip —
                  an invented record on a real table is the one thing two players sitting
                  together can catch instantly. (Phase E, §5.7.) */}
              <div className="rlt-card-hist">
                <span className="rlt-card-hist-k">{hist.length ? 'Last' : 'New table'}</span>
                {hist.map((n, i) => (
                  <span key={i} className={`rlt-card-n rlt-card-n--${colour(n)}`}>{n}</span>
                ))}
              </div>

              <div className="rlt-card-play">
                <PlayIcon />
                <span>PLAY NOW</span>
              </div>

              <div className="rlt-card-live">
                <i className="rlt-card-live-dot" />
                next spin in {left}s
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * THE JOIN SCREEN.
 *
 * Short — two to six seconds, eight at the very outside. The owner cut this
 * hard from the original 3-18: "do not make them hold for long... I don't think
 * users will wait." Long enough to read as a table you are joining rather than
 * one that started when you arrived; short enough that nobody backs out.
 *
 * The table's history is on screen the whole time, so by the moment the board
 * appears the player has already watched this table have a past.
 */
export function Joining({ table, onReady, hist: liveHist }: {
  table: Table; onReady: () => void;
  /** shared mode: the table's REAL last results, so the wait is not spent reading a fiction */
  hist?: number[];
}) {
  const [endsAt] = useState(() => Date.now() + joinWaitMs(table, Date.now()));
  const now = useSecond();
  const left = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));

  useEffect(() => {
    const t = window.setTimeout(onReady, Math.max(0, endsAt - Date.now()));
    return () => window.clearTimeout(t);
  }, [endsAt, onReady]);

  const hist = liveHist ? liveHist.slice(0, 12) : recentResults(table, now, 12);
  return (
    <div className="rlt-lobby rlt-lobby--join">
      <div className="rlt-join-card">
        <div className="rlt-join-name">
          {table.name}
          {table.fast && <span className="rlt-card-pace quick">⚡ QUICK</span>}
        </div>
        <div className="rlt-card-id rlt-join-id">#{table.id}</div>

        <div className="rlt-join-ring">
          <span className="rlt-join-secs">{left}</span>
          <span className="rlt-join-unit">sec</span>
        </div>

        <div className="rlt-join-msg">Taking your seat…</div>

        <div className="rlt-join-hist-k">Last results at this table</div>
        <div className="rlt-join-hist">
          {hist.map((n, i) => (
            <span key={i} className={`rlt-card-n rlt-card-n--${colour(n)}`}>{n}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
